import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { trpc } from "@/lib/trpc";
import { fullCustomerSearchHits } from "@shared/customerSearchAccess";
import { customerDuplicateCheckCandidates } from "@shared/customerCreationDuplicateReview";
import { toast } from "sonner";
import { AlertCircle, AlertTriangle, CheckCircle2, ChevronLeft, Upload, X, Plus } from "lucide-react";
import { useEffect, useRef, useState, useCallback } from "react";
import { useLocation } from "wouter";
import { usePathname, navigate } from "wouter/use-browser-location";
import { useAuth } from "@/_core/hooks/useAuth";
import { normaliseBrand, normaliseModel } from "@/lib/modelNormalise";
import { PRODUCT_CATEGORIES } from "../../../drizzle/schema";

interface CustomerMatch {
  id: number;
  name: string;
  email?: string | null;
  contact?: string | null;
  address?: string | null;
  unitNumber?: string | null;
  postalCode?: string | null;
  salespersonId?: number | null;
  salespersonName?: string | null;
}

interface ProductMatch {
  product: { id: number; brand: string; model: string; serialNumber: string; customerId?: number | null };
  jobs: Array<{ id: number; jobNumber: string; status: string; faultReported: string; createdAt: Date }>;
  customer: { id: number; name: string } | null;
}

// Abandon confirmation dialog
function AbandonDialog({ onAbandon, onStay }: { onAbandon: () => void; onStay: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-4">
        <h2 className="text-lg font-semibold mb-2">You have an unsaved report.</h2>
        <p className="text-sm text-muted-foreground mb-5">
          Your changes have not been saved.
        </p>
        <div className="flex gap-3 justify-end">
          <Button variant="outline" onClick={onAbandon}>Leave</Button>
          <Button className="bg-black text-white hover:bg-gray-800" onClick={onStay}>Save</Button>
        </div>
      </div>
    </div>
  );
}

// Create customer dialog
function CreateCustomerDialog({
  isOpen,
  onClose,
  onSuccess,
  staffList,
  salesStaff,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (customerId: number) => void;
  staffList: Array<{ id: number; name: string; role?: string }> | undefined;
  salesStaff: Array<{ id: number; name: string | null }> | undefined;
}) {
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    phone: "",
    email: "",
    unitNumber: "",
    postalCode: "",
    salespersonId: "",
  });

  const createCustomer = trpc.customers.create.useMutation({
    onSuccess: (data) => {
      toast.success("Customer created successfully");
      onSuccess(data.id);
      setFormData({ name: "", address: "", phone: "", email: "", unitNumber: "", postalCode: "", salespersonId: "" });
      onClose();
    },
    onError: (err) => {
      toast.error(err.message || "Failed to create customer");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.address.trim() || !formData.salespersonId) {
      toast.error("Please fill in required fields: Name, Address, and PIC");
      return;
    }
    createCustomer.mutate({
      name: formData.name,
      address: formData.address,
      contact: formData.phone || undefined,
      email: formData.email || undefined,
      unitNumber: formData.unitNumber || undefined,
      postalCode: formData.postalCode || undefined,
      salespersonId: parseInt(formData.salespersonId),
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-semibold mb-4">Create New Customer</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="create-name">Full Name *</Label>
            <Input
              id="create-name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g. Alex Chew"
              required
              className="mt-1.5"
            />
          </div>

          <div>
            <Label htmlFor="create-address">Address *</Label>
            <Input
              id="create-address"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              placeholder="e.g. 123 Main Street"
              required
              className="mt-1.5"
            />
          </div>

          <div>
            <Label htmlFor="create-postal">Postal Code</Label>
            <Input
              id="create-postal"
              value={formData.postalCode}
              onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
              placeholder="e.g. 123456"
              className="mt-1.5"
            />
          </div>

          <div>
            <Label htmlFor="create-unit">Unit Number</Label>
            <Input
              id="create-unit"
              value={formData.unitNumber}
              onChange={(e) => setFormData({ ...formData, unitNumber: e.target.value })}
              placeholder="e.g. #01-23"
              className="mt-1.5"
            />
          </div>

          <div>
            <Label htmlFor="create-phone">Phone</Label>
            <Input
              id="create-phone"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="e.g. 6512345678"
              className="mt-1.5"
            />
          </div>

          <div>
            <Label htmlFor="create-email">Email</Label>
            <Input
              id="create-email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="e.g. alex@example.com"
              className="mt-1.5"
            />
          </div>

          <div>
            <Label htmlFor="create-pic">Assign PIC (Salesperson) *</Label>
            <select
              id="create-pic"
              value={formData.salespersonId}
              onChange={(e) => setFormData({ ...formData, salespersonId: e.target.value })}
              className="mt-1.5 w-full border border-input rounded-md px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              required
            >
              <option value="">Select a salesperson...</option>
              {(salesStaff ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name ?? "(no name)"}
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-3 justify-end pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createCustomer.isPending}
              className="bg-black text-white hover:bg-gray-800"
            >
              {createCustomer.isPending ? "Creating..." : "Create Customer"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function NewJob() {
  const [, setLocation] = useLocation();
  const [searchParams] = useLocation();
  const pathname = usePathname();
  const { user } = useAuth();

  // Parse query parameters for pre-population from customer profile
  const queryParams = new URLSearchParams(searchParams.split('?')[1] || '');
  const prefilledCustomerId = queryParams.get('customerId');
  const prefilledCustomerName = queryParams.get('customerName');
  const prefilledCustomerEmail = queryParams.get('customerEmail');
  const prefilledCustomerContact = queryParams.get('customerContact');
  const prefilledCustomerAddress = queryParams.get('customerAddress');
  const prefilledUnitNumber = queryParams.get('unitNumber');
  const prefilledPostalCode = queryParams.get('postalCode');

  // Form state - initialize with pre-filled values from query parameters
  const [reportedBy, setReportedBy] = useState("");
  const [salespersonId, setSalespersonId] = useState("");
  const [customerName, setCustomerName] = useState(prefilledCustomerName || "");
  const [customerContact, setCustomerContact] = useState(prefilledCustomerContact || "");
  const [customerEmail, setCustomerEmail] = useState(prefilledCustomerEmail || "");
  const [customerAddress, setCustomerAddress] = useState(prefilledCustomerAddress || "");
  const [unitNumber, setUnitNumber] = useState(prefilledUnitNumber || "");
  const [postalCode, setPostalCode] = useState(prefilledPostalCode || "");
  const [postalLookupLoading, setPostalLookupLoading] = useState(false);

  const [brand, setBrand] = useState("");
  const [model, setModel] = useState("");
  const [category, setCategory] = useState("");
  const [serialNumber, setSerialNumber] = useState("");
  const [underWarranty, setUnderWarranty] = useState<"yes" | "no">("no");
  const [warrantyExpiry, setWarrantyExpiry] = useState("");
  const [faultReported, setFaultReported] = useState("");
  const [estimatedCost, setEstimatedCost] = useState("");
  const [consentGiven, setConsentGiven] = useState(false);
  const [commsPref, setCommsPref] = useState<"whatsapp" | "email" | "both">("both");
  const [collectionPreference, setCollectionPreference] = useState<"delivery" | "self_pickup">("self_pickup");
  const [photos, setPhotos] = useState<Array<{ file: File; preview: string }>>([]);

  // Navigation guard state
  const [showAbandon, setShowAbandon] = useState(false);
  const pendingNavTarget = useRef<string | null>(null);
  const submittedRef = useRef(false);

  // Duplicate detection state - initialize with pre-filled customer ID
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | undefined>(
    prefilledCustomerId ? parseInt(prefilledCustomerId) : undefined
  );
  const [selectedProductId, setSelectedProductId] = useState<number | undefined>();
  const [customerDuplicates, setCustomerDuplicates] = useState<CustomerMatch[]>([]);
  const [serialDuplicate, setSerialDuplicate] = useState<ProductMatch | null>(null);

  // Create customer dialog state
  const [showCreateCustomerDialog, setShowCreateCustomerDialog] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Determine if the form has been touched (any field filled)
  const isDirty =
    customerName.trim() !== "" ||
    customerContact.trim() !== "" ||
    customerEmail.trim() !== "" ||
    brand.trim() !== "" ||
    model.trim() !== "" ||
    serialNumber.trim() !== "" ||
    faultReported.trim() !== "";

  // Navigation guard: intercept browser back / unload
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty && !submittedRef.current) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  // Guarded navigate helper — used by back button and Cancel button
  const guardedNavigate = useCallback(
    (target: string) => {
      if (isDirty && !submittedRef.current) {
        pendingNavTarget.current = target;
        setShowAbandon(true);
      } else {
        setLocation(target);
      }
    },
    [isDirty, setLocation]
  );

  const lookupPostalCode = async (code: string) => {
    if (code.length !== 6 || !/^\d{6}$/.test(code)) return;
    setPostalLookupLoading(true);
    try {
      const res = await fetch(
        `https://www.onemap.gov.sg/api/common/elastic/search?searchVal=${code}&returnGeom=N&getAddrDetails=Y&pageNum=1`
      );
      const data = await res.json();
      if (data.results && data.results.length > 0) {
        const r = data.results[0];
        const block = r.BLK_NO && r.BLK_NO !== "NIL" ? `${r.BLK_NO} ` : "";
        const street = r.ROAD_NAME && r.ROAD_NAME !== "NIL" ? r.ROAD_NAME : "";
        const building =
          r.BUILDING && r.BUILDING !== "NIL" && r.BUILDING !== r.ROAD_NAME
            ? ` ${r.BUILDING}`
            : "";
        const address = `${block}${street}${building}, Singapore ${code}`.trim();
        setCustomerAddress(address);
        toast.success("Address auto-filled from postal code");
      } else {
        toast.error("Postal code not found");
      }
    } catch {
      toast.error("Could not look up postal code");
    } finally {
      setPostalLookupLoading(false);
    }
  };

  // Auto-select logged-in user as reportedBy
  useEffect(() => {
    if (user?.name && !reportedBy) {
      setReportedBy(user.name);
    }
  }, [user?.name]);

  // Data queries
  const { data: staffList } = trpc.staff.list.useQuery();
  const { data: salesStaff } = trpc.customers.listSalesStaff.useQuery();
  const selectedCustomer = trpc.customers.getById.useQuery(
    { id: selectedCustomerId! },
    { enabled: !!selectedCustomerId },
  );
  const { data: settings } = trpc.settings.getAll.useQuery();
  const consentThreshold = settings?.consent_threshold || "500";

  // Live customer search autocomplete
  const { data: customerSearchResultsRaw } = trpc.customers.search.useQuery(
    { query: customerName },
    { enabled: customerName.length > 1 && !selectedCustomerId }
  );
  const customerSearchResults = fullCustomerSearchHits(customerSearchResultsRaw);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);

  // Owner rule: the RO PIC inherits the customer's PIC. When the selected customer has an
  // eligible PIC, the field is locked to that PIC (admins can change it later on the RO page).
  const customerPicIdRaw = selectedCustomerId ? selectedCustomer.data?.customer?.salespersonId : null;
  const inheritedCustomerPicId =
    customerPicIdRaw && (salesStaff ?? []).some((s) => s.id === customerPicIdRaw) ? customerPicIdRaw : null;
  useEffect(() => {
    if (inheritedCustomerPicId && salespersonId !== String(inheritedCustomerPicId)) {
      setSalespersonId(String(inheritedCustomerPicId));
    }
  }, [inheritedCustomerPicId, salespersonId]);

  // Duplicate checks
  const { data: duplicateCheck } = trpc.customers.checkDuplicates.useQuery(
    { name: customerName, email: customerEmail || undefined, contact: customerContact || undefined },
    { enabled: customerName.length > 2 && !selectedCustomerId }
  );

  const { data: serialCheck } = trpc.products.checkSerial.useQuery(
    { serialNumber },
    { enabled: serialNumber.length > 3 && !selectedProductId }
  );

  useEffect(() => {
    const candidates = customerDuplicateCheckCandidates(duplicateCheck);
    if (candidates.length > 0 && !selectedCustomerId) {
      setCustomerDuplicates(candidates as CustomerMatch[]);
    } else {
      setCustomerDuplicates([]);
    }
  }, [duplicateCheck, selectedCustomerId]);

  useEffect(() => {
    if (serialCheck && !selectedProductId) {
      setSerialDuplicate(serialCheck as unknown as ProductMatch);
    } else {
      setSerialDuplicate(null);
    }
  }, [serialCheck, selectedProductId]);

  // Mutations
  const createJob = trpc.jobs.create.useMutation({
    onSuccess: ({ jobNumber, jobId }) => {
      submittedRef.current = true;
      toast.success(`Job ${jobNumber} created successfully`);
      navigate(`/repair-orders/id/${jobId}`, { state: { from: pathname } });
    },
    onError: (err) => {
      toast.error(err.message || "Failed to create job");
    },
  });

  const uploadPhoto = trpc.jobs.uploadPhoto.useMutation();

  const handlePhotoAdd = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const newPhotos = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    setPhotos((prev) => [...prev, ...newPhotos]);
  };

  const handlePhotoRemove = (index: number) => {
    setPhotos((prev) => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleUseExistingCustomer = (customer: CustomerMatch) => {
    setSelectedCustomerId(customer.id);
    setCustomerName(customer.name);
    setCustomerContact(customer.contact || "");
    setCustomerEmail(customer.email || "");
    setCustomerAddress(customer.address || "");
    setUnitNumber(customer.unitNumber || "");
    setPostalCode(customer.postalCode || "");
    setSalespersonId(customer.salespersonId ? String(customer.salespersonId) : "");
    setCustomerDuplicates([]);
    // Auto-set commsPref based on available contact details
    const hasPhone = !!(customer.contact);
    const hasEmail = !!(customer.email);
    if (hasPhone && hasEmail) setCommsPref("both");
    else if (hasPhone) setCommsPref("whatsapp");
    else if (hasEmail) setCommsPref("email");
  };

  const handleUseExistingProduct = () => {
    if (serialDuplicate) {
      setSelectedProductId(serialDuplicate.product.id);
      setBrand(serialDuplicate.product.brand);
      setModel(serialDuplicate.product.model);
    }
  };

  const handleCustomerCreated = (customerId: number) => {
    setSelectedCustomerId(customerId);
    setShowCreateCustomerDialog(false);
    setCustomerDuplicates([]);
  };

  // Normalise brand/model on blur
  const handleBrandBlur = () => {
    if (brand) setBrand(normaliseBrand(brand));
  };
  const handleModelBlur = () => {
    if (model) setModel(normaliseModel(model, brand));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!reportedBy) {
      toast.error("Please select who is reporting this job");
      return;
    }
    if (!salespersonId) {
      toast.error("Please select the Repair Order PIC");
      return;
    }

    const normalisedBrand = normaliseBrand(brand);
    const normalisedModel = normaliseModel(model, normalisedBrand);

    const jobResult = await createJob.mutateAsync({
      customerId: selectedCustomerId,
      customerName,
      customerContact: customerContact || undefined,
      customerEmail: customerEmail || undefined,
      customerAddress: customerAddress || undefined,
      customerUnitNumber: unitNumber || undefined,
      customerPostalCode: postalCode || undefined,
      salespersonId: Number(salespersonId),
      productId: selectedProductId,
      brand: normalisedBrand,
      model: normalisedModel,
      category: category || undefined,
      serialNumber,
      reportedBy,
      warrantyAtIntake: underWarranty,
      warrantyExpiry: warrantyExpiry ? new Date(warrantyExpiry) : undefined,
      faultReported,
      estimatedCost: estimatedCost || undefined,
      consentGiven,
      collectionPreference,
      commsPref,
    });

    // Upload photos
    if (photos.length > 0 && jobResult.jobId) {
      for (const photo of photos) {
        const reader = new FileReader();
        await new Promise<void>((resolve) => {
          reader.onload = async () => {
            const base64 = (reader.result as string).split(",")[1];
            await uploadPhoto.mutateAsync({
              jobId: jobResult.jobId,
              filename: photo.file.name,
              contentType: photo.file.type,
              base64,
            });
            resolve();
          };
          reader.readAsDataURL(photo.file);
        });
      }
    }
  };

  const costExceedsConsent =
    estimatedCost && parseFloat(estimatedCost) > parseFloat(consentThreshold);

  return (
    <DashboardLayout>
      {showAbandon && (
        <AbandonDialog
          onAbandon={() => {
            setShowAbandon(false);
            if (pendingNavTarget.current) setLocation(pendingNavTarget.current);
          }}
          onStay={() => {
            setShowAbandon(false);
            pendingNavTarget.current = null;
          }}
        />
      )}

      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => guardedNavigate("/repair-orders")}
            className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">New Repair Order</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Log a new item for service
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Staff */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Staff Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="reportedBy">Reported By *</Label>
                  <select
                    id="reportedBy"
                    value={reportedBy}
                    onChange={(e) => setReportedBy(e.target.value)}
                    className="mt-1.5 w-full border border-input rounded-md px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                    required
                  >
                    <option value="">Select staff member...</option>
                    {user?.name && !staffList?.some((s) => s.name === user.name) && (
                      <option value={user.name}>{user.name}</option>
                    )}
                    {staffList?.map((s) => (
                      <option key={s.id} value={s.name}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="salespersonId">PIC (Salesperson) *</Label>
                  <select
                    id="salespersonId"
                    value={salespersonId}
                    onChange={(e) => setSalespersonId(e.target.value)}
                    className="mt-1.5 w-full border border-input rounded-md px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-70"
                    disabled={!!inheritedCustomerPicId}
                    required
                  >
                    <option value="">Select the RO PIC...</option>
                    {(salesStaff ?? []).map((staff) => (
                      <option key={staff.id} value={staff.id}>{staff.name ?? "(no name)"}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-muted-foreground">{inheritedCustomerPicId ? "Inherited from the customer’s PIC. An admin can change the RO PIC on the Repair Order page." : "Defaults to the customer’s PIC when available. Choose a Sales/Admin PIC if the customer has none."}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Customer Information */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Customer Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {selectedCustomerId && (
                <div className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded-md text-sm text-green-700">
                  <CheckCircle2 className="h-4 w-4 shrink-0" />
                  <span>Using existing customer record</span>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedCustomerId(undefined);
                      setCustomerDuplicates([]);
                      setSalespersonId("");
                    }}
                    className="ml-auto text-green-600 hover:text-green-800"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}

              {/* Duplicate customer alert */}
              {customerDuplicates.length > 0 && !selectedCustomerId && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
                  <div className="flex items-center gap-2 text-amber-700 text-sm font-medium mb-2">
                    <AlertTriangle className="h-4 w-4" />
                    Possible duplicate customer found
                  </div>
                  {customerDuplicates.map((c) => (
                    <div
                      key={c.id}
                      className="flex items-center justify-between p-2 bg-white border rounded text-sm mb-1 last:mb-0"
                    >
                      <div>
                        <span className="font-medium">{c.name}</span>
                        {c.email && (
                          <span className="text-muted-foreground ml-2">{c.email}</span>
                        )}
                        {c.contact && (
                          <span className="text-muted-foreground ml-2">{c.contact}</span>
                        )}
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => handleUseExistingCustomer(c)}
                      >
                        Use this record
                      </Button>
                    </div>
                  ))}
                  <div className="flex gap-2 mt-2">
                    <button
                      type="button"
                      className="text-xs text-amber-600 underline"
                      onClick={() => setCustomerDuplicates([])}
                    >
                      No, this is a different customer
                    </button>
                  </div>
                </div>
              )}

              {/* No duplicates found - offer to create new customer */}
              {customerName.length > 2 && customerDuplicates.length === 0 && !selectedCustomerId && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-blue-700">Customer not found in database</span>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setShowCreateCustomerDialog(true)}
                      className="text-xs"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Create new customer
                    </Button>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="relative">
                  <Label htmlFor="customerName">Full Name *</Label>
                  <Input
                    id="customerName"
                    value={customerName}
                    onChange={(e) => {
                      setCustomerName(e.target.value);
                      setSelectedCustomerId(undefined);
                      setShowCustomerDropdown(true);
                    }}
                    onFocus={() => setShowCustomerDropdown(true)}
                    onBlur={() => setTimeout(() => setShowCustomerDropdown(false), 200)}
                    placeholder="Type to search existing customers..."
                    required
                    className="mt-1.5"
                    autoComplete="off"
                  />
                  {showCustomerDropdown && customerSearchResults && customerSearchResults.length > 0 && !selectedCustomerId && (
                    <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                      {customerSearchResults.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b last:border-0 text-sm"
                          onMouseDown={() => {
                            handleUseExistingCustomer(c as any);
                            setShowCustomerDropdown(false);
                          }}
                        >
                          <div className="font-medium">{c.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {[c.contact, c.address].filter(Boolean).join(" · ")}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <Label htmlFor="customerContact">Contact Number</Label>
                  <Input
                    id="customerContact"
                    value={customerContact}
                    onChange={(e) => {
                      setCustomerContact(e.target.value);
                      setSelectedCustomerId(undefined);
                    }}
                    placeholder="e.g. +65 9123 4567"
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="customerEmail">Email Address</Label>
                  <Input
                    id="customerEmail"
                    type="email"
                    value={customerEmail}
                    onChange={(e) => {
                      setCustomerEmail(e.target.value);
                      setSelectedCustomerId(undefined);
                    }}
                    placeholder="e.g. alex@email.com"
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="unitNumber">Unit Number</Label>
                  <Input
                    id="unitNumber"
                    value={unitNumber}
                    onChange={(e) => setUnitNumber(e.target.value)}
                    placeholder="e.g. #12-34"
                    className="mt-1.5"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label>Address</Label>
                  <div className="mt-1.5 flex gap-2">
                    <div className="w-36 shrink-0">
                      <Input
                        value={postalCode}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, "").slice(0, 6);
                          setPostalCode(val);
                          if (val.length === 6) lookupPostalCode(val);
                        }}
                        placeholder="Postal code"
                        maxLength={6}
                        disabled={postalLookupLoading}
                      />
                    </div>
                    <Input
                      id="customerAddress"
                      value={customerAddress}
                      onChange={(e) => setCustomerAddress(e.target.value)}
                      placeholder={
                        postalLookupLoading
                          ? "Looking up..."
                          : "Auto-filled from postal code"
                      }
                      disabled={postalLookupLoading}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Enter 6-digit postal code to auto-fill street address.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Product Information */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Product Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Serial number duplicate alert */}
              {serialDuplicate && !selectedProductId && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="flex items-center gap-2 text-blue-700 text-sm font-medium mb-2">
                    <AlertCircle className="h-4 w-4" />
                    This serial number is already in the system
                  </div>
                  <div className="bg-white border rounded p-2 text-sm mb-2">
                    <div className="font-medium">
                      {serialDuplicate.product.brand} {serialDuplicate.product.model}
                    </div>
                    {serialDuplicate.customer && (
                      <div className="text-muted-foreground text-xs mt-0.5">
                        Owner: {serialDuplicate.customer.name}
                      </div>
                    )}
                    {serialDuplicate.jobs.length > 0 && (
                      <div className="text-muted-foreground text-xs mt-0.5">
                        {serialDuplicate.jobs.length} previous repair order
                        {serialDuplicate.jobs.length !== 1 ? "s" : ""}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={handleUseExistingProduct}
                    >
                      Use existing product record
                    </Button>
                    <button
                      type="button"
                      className="text-xs text-blue-600 underline"
                      onClick={() => setSelectedProductId(undefined)}
                    >
                      Create new record
                    </button>
                  </div>
                </div>
              )}

              {selectedProductId && (
                <div className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded-md text-sm text-green-700">
                  <CheckCircle2 className="h-4 w-4 shrink-0" />
                  <span>Using existing product record</span>
                  <button
                    type="button"
                    onClick={() => setSelectedProductId(undefined)}
                    className="ml-auto text-green-600 hover:text-green-800"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="brand">Brand *</Label>
                  <Input
                    id="brand"
                    value={brand}
                    onChange={(e) => setBrand(e.target.value)}
                    onBlur={handleBrandBlur}
                    placeholder="e.g. Mark Levinson"
                    required
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="model">Model *</Label>
                  <Input
                    id="model"
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                    onBlur={handleModelBlur}
                    placeholder="e.g. No. 333"
                    required
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="serialNumber">Serial Number *</Label>
                  <Input
                    id="serialNumber"
                    value={serialNumber}
                    onChange={(e) => {
                      setSerialNumber(e.target.value);
                      setSelectedProductId(undefined);
                    }}
                    placeholder="e.g. SN123456789"
                    required
                    className="mt-1.5"
                  />
                </div>
                <div className="sm:col-span-3">
                  <Label htmlFor="category">Product Category</Label>
                  <select
                    id="category"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="mt-1.5 w-full border border-input rounded-md px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="">— Select category (optional) —</option>
                    {PRODUCT_CATEGORIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Warranty */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Under Warranty *</Label>
                  <div className="flex gap-3 mt-1.5">
                    <button
                      type="button"
                      onClick={() => setUnderWarranty("yes")}
                      className={`flex-1 py-2 text-sm border rounded-md transition-colors ${
                        underWarranty === "yes"
                          ? "bg-black text-white border-black"
                          : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
                      }`}
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      onClick={() => setUnderWarranty("no")}
                      className={`flex-1 py-2 text-sm border rounded-md transition-colors ${
                        underWarranty === "no"
                          ? "bg-black text-white border-black"
                          : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
                      }`}
                    >
                      No
                    </button>
                  </div>
                </div>
                {underWarranty === "yes" && (
                  <div>
                    <Label htmlFor="warrantyExpiry">Warranty Expiry Date</Label>
                    <Input
                      id="warrantyExpiry"
                      type="date"
                      value={warrantyExpiry}
                      onChange={(e) => setWarrantyExpiry(e.target.value)}
                      className="mt-1.5"
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Service Details */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Service Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="faultReported">Fault Reported *</Label>
                <Textarea
                  id="faultReported"
                  value={faultReported}
                  onChange={(e) => setFaultReported(e.target.value)}
                  placeholder="Describe the fault or issue reported by the customer..."
                  rows={3}
                  required
                  className="mt-1.5"
                />
              </div>

              {/* Consent */}
              <div className="p-3 bg-gray-50 border rounded-md">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={consentGiven}
                    onChange={(e) => setConsentGiven(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300"
                  />
                  <div>
                    <span className="text-sm font-medium">
                      Customer consents to proceed if repair cost is below ${consentThreshold}
                    </span>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      By checking this box, you confirm that the customer has given verbal or written
                      consent to proceed with the repair if the total cost does not exceed $
                      {consentThreshold}.
                    </p>
                  </div>
                </label>
              </div>

              {/* Communication Preference */}
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                <Label className="text-sm font-medium text-blue-900 mb-2 block">
                  Customer Notification Channel
                </Label>
                <p className="text-xs text-muted-foreground mb-3">
                  How should we send the receipt and status updates to the customer?
                </p>
                <div className="flex gap-2">
                  {(["both", "whatsapp", "email"] as const).map((pref) => (
                    <button
                      key={pref}
                      type="button"
                      onClick={() => setCommsPref(pref)}
                      className={`flex-1 py-2 text-xs font-medium border rounded-md transition-colors ${
                        commsPref === pref
                          ? "bg-black text-white border-black"
                          : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
                      }`}
                    >
                      {pref === "both" ? "WhatsApp + Email" : pref === "whatsapp" ? "WhatsApp Only" : "Email Only"}
                    </button>
                  ))}
                </div>
                {commsPref !== "email" && !customerContact && (
                  <p className="text-xs text-amber-600 mt-2">⚠️ No WhatsApp number on file — add a contact number above.</p>
                )}
                {commsPref !== "whatsapp" && !customerEmail && (
                  <p className="text-xs text-amber-600 mt-2">⚠️ No email address on file — add an email above.</p>
                )}
              </div>

              {/* Collection Preference */}
              <div>
                <Label>Collection Preference *</Label>
                <div className="flex gap-3 mt-1.5">
                  <button
                    type="button"
                    onClick={() => setCollectionPreference("delivery")}
                    className={`flex-1 py-2.5 text-sm border rounded-md transition-colors ${
                      collectionPreference === "delivery"
                        ? "bg-black text-white border-black"
                        : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
                    }`}
                  >
                    Delivery to Address
                  </button>
                  <button
                    type="button"
                    onClick={() => setCollectionPreference("self_pickup")}
                    className={`flex-1 py-2.5 text-sm border rounded-md transition-colors ${
                      collectionPreference === "self_pickup"
                        ? "bg-black text-white border-black"
                        : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
                    }`}
                  >
                    Self Pick-Up
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Photo Upload */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Photos (Optional)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div
                className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center cursor-pointer hover:border-gray-400 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                <p className="text-sm text-muted-foreground">
                  Click to upload photos of the item
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  PNG, JPG, WEBP up to 10MB each
                </p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handlePhotoAdd}
              />
              {photos.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-4">
                  {photos.map((photo, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={photo.preview}
                        alt={`Photo ${index + 1}`}
                        className="w-full h-24 object-cover rounded-md border"
                      />
                      <button
                        type="button"
                        onClick={() => handlePhotoRemove(index)}
                        className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex gap-3 pb-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => guardedNavigate("/repair-orders")}
              className="flex-1 sm:flex-none"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createJob.isPending}
              className="flex-1 sm:flex-none bg-black hover:bg-gray-800"
            >
              {createJob.isPending ? "Creating..." : "Create Repair Order"}
            </Button>
          </div>
        </form>
      </div>

      {/* Create Customer Dialog */}
      <CreateCustomerDialog
        isOpen={showCreateCustomerDialog}
        onClose={() => setShowCreateCustomerDialog(false)}
        onSuccess={handleCustomerCreated}
        staffList={staffList}
        salesStaff={salesStaff}
      />

      {/* Abandon Dialog */}
      {showAbandon && (
        <AbandonDialog
          onAbandon={() => {
            submittedRef.current = true;
            setLocation(pendingNavTarget.current || "/repair-orders");
          }}
          onStay={() => setShowAbandon(false)}
        />
      )}
    </DashboardLayout>
  );
}
